//====== WR data 15/09/2026 — SmartFlow Item Importer (Fase 3)
// Motivo da alteração: gravação REAL do Item (Material) no SAP Business One (Service Layer),
// mesmo padrão de smartflowSapWrite.js (Parceiro) — SAP mandante, MongoDB (SmartFlowItemStaging,
// via smartflow-backend) o espelho. Bem mais simples que Parceiro: sem endereço, sem fiscal, sem
// País/Estado/Município — Item usa só os campos confirmados na Fase 1 (seção 3.4 do plano):
// ItemCode, ItemName, ItemsGroupCode, InventoryUOM, InventoryItem/SalesItem/PurchaseItem.
//
// Campos obrigatórios REAIS do tenant só se confirmam testando de verdade na Service Layer
// (Fase 4, plano seção 3.4) — este payload é o ponto de partida, não a palavra final.
//
// Dedup: o papel do CNPJ/CPF (Parceiro) aqui é o código de barras (EAN, campo BarCode do Item).
//======
import apiSap from "services/sapApi"
import { marcarItemGravadoNoSap } from "services/smartflow/itemImporterApi"

function sapUrl(serviceLayerUrl, path) {
  return String(serviceLayerUrl || "").replace(/\/$/, "") + path
}

//====== Lista real de Grupos de Item do tenant do projeto (GET /ItemGroups) — combobox de Grupo
// padrão no modal "Alterando Projeto" e referência que a IA usa pra sugerir (nunca inventa um
// grupo que não exista).
// @return {Promise<Array<{code:number, name:string}>>}
export async function buscarGruposItemSap(serviceLayerUrl) {
  const { data } = await apiSap.get(
    sapUrl(serviceLayerUrl, "/ItemGroups?$select=Number,GroupName&$orderby=GroupName")
  )
  return (data?.value || []).map((grupo) => ({ code: grupo.Number, name: grupo.GroupName }))
}

//====== WR data 25/09/2026 — Pré-requisitos (BBP): lista real de Depósitos do tenant do projeto
// (GET /Warehouses) — usada pra mesclar com a sugestão do BBP na tela de Pré-requisitos (o que já
// existe no SAP entra marcado como "já cadastrado no SAP", diferente do que só o BBP sugere).
// @return {Promise<Array<{code:string, name:string}>>}
export async function buscarDepositosSap(serviceLayerUrl) {
  const { data } = await apiSap.get(
    sapUrl(serviceLayerUrl, "/Warehouses?$select=WarehouseCode,WarehouseName&$orderby=WarehouseName")
  )
  return (data?.value || []).map((deposito) => ({ code: deposito.WarehouseCode, name: deposito.WarehouseName }))
}

//====== Unidades de Medida cadastradas no tenant (GET /UnitOfMeasurements) — nem toda instalação
// do SAP tem a gestão de UoM ativada; sem a entidade, devolve lista vazia (o campo vira texto
// livre na tela, sem travar o fluxo).
// @return {Promise<Array<{code:string, name:string}>>}
export async function buscarUnidadesMedidaSap(serviceLayerUrl) {
  try {
    const { data } = await apiSap.get(
      sapUrl(serviceLayerUrl, "/UnitOfMeasurements?$select=Code,Name&$orderby=Name")
    )
    return (data?.value || []).map((uom) => ({ code: uom.Code, name: uom.Name }))
  } catch (error) {
    console.warn("[SmartFlow] UnitOfMeasurements indisponível neste tenant (Unidade vira texto livre):", error?.message)
    return []
  }
}

function extrairMensagemErroSap(error) {
  return (
    error?.response?.data?.error?.message?.value ||
    error?.response?.data?.error?.message ||
    error?.message ||
    "Falha ao gravar no SAP."
  )
}

// GET por ItemCode: só um "não encontrado" de verdade (error.warning, sinal do -2028 tratado pelo
// interceptor do apiSap) cai pro POST; qualquer outro erro é repropagado.
async function itemJaExisteNoSap(serviceLayerUrl, itemCode) {
  try {
    await apiSap.get(sapUrl(serviceLayerUrl, `/Items('${itemCode}')`))
    return true
  } catch (error) {
    if (error?.warning) return false
    throw error
  }
}

//====== Papel do buscarCardCodePorCnpj (Parceiro), pro lado de Item: procura pelo código de
// barras (BarCode) e devolve o ItemCode real, se já existir sob outro código. Sem match / erro ->
// null — nunca bloqueia o fluxo.
// @param {string} ean
// @return {Promise<string|null>}
export async function buscarItemCodePorEan(serviceLayerUrl, ean) {
  const valor = String(ean || "").trim()
  if (!valor) return null
  try {
    const { data } = await apiSap.get(
      sapUrl(
        serviceLayerUrl,
        `/Items?$select=ItemCode,BarCode&$filter=${encodeURIComponent(`BarCode eq '${valor}'`)}&$top=1`
      )
    )
    return data?.value?.[0]?.ItemCode || null
  } catch (error) {
    console.error("[SmartFlow] Falha ao procurar Item por código de barras no SAP:", error)
    return null
  }
}

//====== Papel do buscarCardCodesPorDocumentos (Parceiro), pro lado de Item — consulta em LOTE por
// código de barras, pra "Preparar dados" já marcar as linhas que vão virar ATUALIZAÇÃO.
// @param {Array<string>} listaEans
// @return {Promise<Object>} { [ean]: itemCode } só dos que já existem no SAP
//====== WR data 16/09/2026 — pedido do usuário: NCM não é gravado no cadastro do Item porque o
// campo é chave estrangeira pra uma tabela de NCM pré-cadastrada no SAP (ver comentário em
// montarPayloadItem). Em vez de replicar essa tabela num Mongo à parte (ela já existe no próprio
// SAP — 11 mil+ códigos oficiais, entidade NCMCodesSetup — e pode variar por tenant), consulta ao
// vivo a Service Layer do projeto, mesmo padrão de buscarGruposItemSap/buscarItemCodesPorEans:
// sempre fiel ao que aquele SAP específico realmente tem cadastrado, sem sincronizar nada.
// NCMCode nessa tabela vem formatado com pontos ("5209.11.00"); o XML da NF-e vem só dígitos
// ("52091100") — formatNcmComPontos() converte antes de filtrar.
//
// WR data 16/09/2026 (correção, mesmo dia) — motivo: reportado ao vivo — "Enter valid code
// [OITM.NCMCode]" continuava acontecendo mesmo pra NCM confirmado como existente na tabela.
// Descoberto testando de verdade: Items.NCMCode não é o próprio código NCM — é o AbsEntry (ID
// interno) do registro em NCMCodesSetup (ex.: NCM "3926.90.90" tem AbsEntry 4497, e é O 4497 que
// precisa ir no campo do Item, não "39269090"). Corrigido pra devolver um MAPA ncm->AbsEntry em
// vez de um Set — o AbsEntry é o que de fato vai no payload.
function formatNcmComPontos(digits) {
  const d = String(digits || "").replace(/\D/g, "")
  if (d.length !== 8) return null
  return `${d.slice(0, 4)}.${d.slice(4, 6)}.${d.slice(6, 8)}`
}

// @param {Array<string>} listaNcmDigits NCM só dígitos (8 dígitos), como vem do XML
// @return {Promise<Object>} { [ncmDigits]: absEntry } só dos NCM que EXISTEM na tabela do SAP —
//   `absEntry` é o valor que de fato vai no campo Items.NCMCode (chave estrangeira).
export async function buscarNcmsValidos(serviceLayerUrl, listaNcmDigits) {
  const unicos = [...new Set((listaNcmDigits || []).map((n) => String(n || "").replace(/\D/g, "")).filter((n) => n.length === 8))]
  if (!unicos.length) return {}

  const validos = {}
  const BLOCO = 15
  for (let i = 0; i < unicos.length; i += BLOCO) {
    const parte = unicos.slice(i, i + BLOCO)
    const filtro = parte.map((d) => `NCMCode eq '${formatNcmComPontos(d)}'`).join(" or ")
    try {
      const { data } = await apiSap.get(
        sapUrl(serviceLayerUrl, `/NCMCodesSetup?$select=AbsEntry,NCMCode&$filter=${encodeURIComponent(filtro)}`)
      )
      ;(data?.value || []).forEach((row) => {
        const digits = String(row.NCMCode || "").replace(/\D/g, "")
        if (digits && row.AbsEntry != null) validos[digits] = row.AbsEntry
      })
    } catch (error) {
      console.error("[SmartFlow] Falha na consulta em lote de NCM no SAP:", error)
    }
  }
  return validos
}

export async function buscarItemCodesPorEans(serviceLayerUrl, listaEans) {
  const unicos = [...new Set((listaEans || []).map((e) => String(e || "").trim()).filter(Boolean))]
  if (!unicos.length) return {}

  const mapa = {}
  const BLOCO = 15
  for (let i = 0; i < unicos.length; i += BLOCO) {
    const parte = unicos.slice(i, i + BLOCO)
    const filtro = parte.map((e) => `BarCode eq '${e}'`).join(" or ")
    try {
      const { data } = await apiSap.get(
        sapUrl(
          serviceLayerUrl,
          `/Items?$select=ItemCode,BarCode&$filter=${encodeURIComponent(filtro)}`
        )
      )
      ;(data?.value || []).forEach((it) => {
        if (it.BarCode && !mapa[it.BarCode]) mapa[it.BarCode] = it.ItemCode
      })
    } catch (error) {
      console.error("[SmartFlow] Falha na consulta em lote por código de barras no SAP:", error)
    }
  }
  return mapa
}

//====== WR data 16/09/2026 — pedido do usuário: campos vistos na tela real do Cadastro do Item
// do SAP que ainda não eram gravados. Nomes de campo confirmados direto no $metadata da Service
// Layer deste tenant E testados com uma gravação real (POST de verdade, depois excluída) antes de
// subir — 2 campos planejados inicialmente não entraram no payload por causa disso:
//   - NCMCode: existe no Item, mas é chave estrangeira pra uma tabela de NCM já cadastrada no SAP
//     ("Enter valid code [OITM.NCMCode]" pra um NCM que não está na tabela desse tenant). WR
//     data 16/09/2026: em vez de nunca mandar, agora só manda quando `item.ncmAbsEntry` existe
//     (buscarNcmsValidos, consulta ao vivo na entidade NCMCodesSetup da Service Layer do projeto,
//     feita em handlePrepararComIA) — sem isso, o NCM fica só informativo (coluna NCM). WR data
//     16/09/2026 (v2, correção): o valor gravado não é o próprio código do NCM, e sim o AbsEntry
//     (ID interno) do registro em NCMCodesSetup — ver comentário completo em buscarNcmsValidos.
//   - U_TX_Origem ("Fonte do produto" no add-on TaxOne/TaxPlus): confirmado no $metadata que esse
//     UDF pertence às entidades Document/StockTransfer, NÃO ao Item — é definido por MOVIMENTO/
//     transação, não faz parte do cadastro mestre do item. NÃO é esse o campo gravado.
//   - ProductSource ("Fonte do produto" na tela do ITEM — nome diferente do UDF acima, mesmo
//     rótulo em português, mas é um campo NATIVO do SAP, não UDF): confirmado no $metadata da
//     entidade Item e testado com um PATCH real (depois desfeito) — aceita string "0" a "8", o
//     mesmo código de origem da mercadoria (tag <orig> da NF-e) que já chega pronto em
//     `item.origemIcms`. WR data 01/10/2026 — reportado ao vivo que vários itens importaram com
//     Origem errada (sempre "0 - Nacional" independente do XML); a causa real dessa vez era outro
//     bug (colisão de ItemCode entre lotes, ver gravarItemNoSap), mas essa investigação revelou
//     que o campo nunca tinha sido gravado de verdade — agora é.
// Os demais confirmados e testados: MaterialType (enum SAPB1.BoMaterialTypes — "Serviços" =
// mt_Service cobre o rádio Serviço/Material da tela, não existe campo separado), WTLiable
// (Sujeito a retenção de imposto — SEMPRE explícito: tYES só quando é Serviço, tNO em todo o
// resto, porque o padrão do SAP vinha marcado errado pra item Material), U_TX_CodigoCest (campo
// do add-on TaxOne/TaxPlus usado por este tenant — NÃO é o CESTCode nativo), ManageSerialNumbers/
// ManageBatchNumbers (Administrar item por), User_Text (aba Observações).
function montarPayloadItem(item, gravadoPor) {
  const isService = item.materialType === "mt_Service"
  return {
    ItemCode: item.itemCode,
    ItemName: item.itemName || undefined,
    ItemsGroupCode: item.itemsGroupCode || undefined,
    InventoryUOM: item.inventoryUOM || undefined,
    InventoryItem: item.inventoryItem || "tYES",
    SalesItem: item.salesItem || "tYES",
    PurchaseItem: item.purchaseItem || "tYES",
    Valid: item.valid || "tYES",
    BarCode: item.cEAN || undefined,
    // NCMCode do Item é o AbsEntry (ID interno) do registro em NCMCodesSetup, NÃO o código NCM em
    // si — ver comentário de buscarNcmsValidos. `ncmAbsEntry` é onde esse valor fica guardado.
    NCMCode: item.ncmAbsEntry != null ? item.ncmAbsEntry : undefined,
    // Origem da mercadoria (tag <orig> da NF-e) — dado exato do XML, não editável na grade.
    ProductSource: item.origemIcms != null && item.origemIcms !== "" ? String(item.origemIcms) : undefined,
    MaterialType: item.materialType || undefined,
    WTLiable: isService ? "tYES" : "tNO",
    U_TX_CodigoCest: item.cestCode || undefined,
    ManageSerialNumbers: item.administrarPor === "serie" ? "tYES" : "tNO",
    ManageBatchNumbers: item.administrarPor === "lote" ? "tYES" : "tNO",
    User_Text: `Importado via SmartFlow por ${gravadoPor || "usuário desconhecido"} em ${new Date().toLocaleString(
      "pt-BR"
    )}.`,
  }
}

//====== Exclui o Item de verdade no SAP — idempotente (404/-2028 não é erro, via error.warning do
// interceptor do apiSap). Exportada separada de excluirItemStaging (backend) — a tela chama as duas.
export async function excluirItemDoSap(itemCode, serviceLayerUrl) {
  try {
    await apiSap.delete(sapUrl(serviceLayerUrl, `/Items('${itemCode}')`))
  } catch (error) {
    if (error?.warning) return
    throw new Error(extrairMensagemErroSap(error))
  }
}

//====== Grava de verdade um Item no SAP — POST (criar) ou PATCH (atualizar), conforme o ItemCode
// já existir. Mesmo tratamento de "já existe" do lado de Parceiro: (1) nosso ItemCode existe ->
// PATCH nele; (2) senão, o código de barras já existe sob outro ItemCode -> PATCH no real; (3)
// senão, erro de verdade. Depois, marca o doc de staging como 'gravado_sap' (via backend).
//
// WR data 01/10/2026 — Motivo da alteração: bug grave reportado ao vivo — um ItemCode GERADO
// AGORA pela sequência (nunca usado antes) colidiu com um item real já existente no SAP (contador
// de sequência desatualizado — ver itemsConsumidosNaSessao em SmartFlowItemImporterForm.js). Como
// o código "já existia", este fluxo caiu no caminho de PATCH e fez uma atualização PARCIAL — só
// com os campos do item novo, deixando os campos que faltavam (ex.: NCM) com o valor do item
// ANTIGO por baixo, intocado. Resultado: um item SAP com uma mistura dos dois, sem nenhum aviso.
// Pra um código RECÉM-GERADO pela sequência, colidir com algo que já existe é sempre sinal de bug
// (sequência desatualizada), nunca uma atualização legítima — por isso agora dá erro alto em vez
// de seguir com o PATCH silencioso.
// @param {Object} item doc completo do SmartFlowItemStaging (camelCase, com _id)
// @param {string|null} gravadoPor e-mail do usuário logado
// @param {string} serviceLayerUrl Service Layer do projeto (login já feito pela tela)
// @param {boolean} usouSequencia true quando o ItemCode foi GERADO agora pela sequência (nunca
//   existiu antes) — vem do chamador porque esse dado não é persistido no staging do Mongo.
// @return {Promise<Object>} doc atualizado + acaoNoSap ("criado"|"atualizado")
export async function gravarItemNoSap(item, gravadoPor, serviceLayerUrl, usouSequencia) {
  const payload = montarPayloadItem(item, gravadoPor)

  const patchAlvo = (codigo) =>
    apiSap.patch(sapUrl(serviceLayerUrl, `/Items('${codigo}')`), { ...payload, ItemCode: codigo })

  const jaExisteNoInicio = await itemJaExisteNoSap(serviceLayerUrl, item.itemCode)
  if (jaExisteNoInicio && usouSequencia) {
    throw new Error(
      `O código "${item.itemCode}" foi gerado agora pela sequência, mas já existe no SAP com outro item — ` +
        `a sequência do projeto está desatualizada (provavelmente por testes anteriores). Avise o ` +
        `responsável antes de continuar: gravar por cima arriscaria misturar os dados dos dois itens.`
    )
  }
  let acao = jaExisteNoInicio ? "atualizado" : "criado"
  let itemCodeFinal = item.itemCode

  const resolverJaExiste = async (erroOriginal) => {
    if (await itemJaExisteNoSap(serviceLayerUrl, item.itemCode)) {
      await patchAlvo(item.itemCode)
      itemCodeFinal = item.itemCode
      acao = "atualizado"
      return
    }
    const real = await buscarItemCodePorEan(serviceLayerUrl, item.cEAN)
    if (real) {
      await patchAlvo(real)
      itemCodeFinal = real
      acao = "atualizado"
      return
    }
    throw erroOriginal
  }

  try {
    if (acao === "atualizado") {
      await patchAlvo(itemCodeFinal)
    } else {
      try {
        await apiSap.post(sapUrl(serviceLayerUrl, "/Items"), payload)
      } catch (postError) {
        const msg = extrairMensagemErroSap(postError)
        if (/j[áa]\s+foi\s+cadastrado|-?2035|already exists|-?5002/i.test(msg)) {
          await resolverJaExiste(postError)
        } else {
          throw postError
        }
      }
    }
  } catch (error) {
    const msg = extrairMensagemErroSap(error)
    //====== WR data 05/10/2026 — reportado ao vivo: origem 7/8 da NF-e ("Selected product source
    // code is not defined") — a lista "Fonte do produto" do SAP só traz 0 a 6 de fábrica. Não dá
    // pra consultar os códigos válidos pela Service Layer, então a checagem é pelo erro real do SAP.
    if (/product source code is not defined/i.test(msg)) {
      throw new Error(
        `Origem ${item.origemIcms ?? "?"} (Fonte do produto) não está cadastrada no SAP deste tenant — ` +
          `cadastre o código em Fonte do produto → "Definir novo" e grave de novo. (SAP: ${msg})`
      )
    }
    throw new Error(msg)
  }

  const atualizado = await marcarItemGravadoNoSap(item._id, {
    gravadoPor: gravadoPor || null,
    itemCode: itemCodeFinal,
  })

  return { ...(atualizado || item), itemCode: itemCodeFinal, acaoNoSap: acao }
}
